import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  listAppSuiteProjects,
  createAppSuiteProject,
  deleteAppSuiteProject,
  getAppSuiteActiveClientId,
  type AppSuiteProject,
} from '../services/appsuiteApi'
import './AppSuitePublishedAssetsPage.css'
import './AppSuiteProjectsPage.css'

function formatDate(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

function statusBadgeClass(status?: string): string {
  if (status === 'editing') return 'appsuite-proj-status appsuite-proj-status--editing'
  if (status === 'published') return 'appsuite-proj-status appsuite-proj-status--published'
  return 'appsuite-proj-status'
}

// ── New Project Dialog ────────────────────────────────────────────────────────
function NewProjectDialog({
  onConfirm,
  onCancel,
  busy,
}: {
  onConfirm: (name: string) => void
  onCancel: () => void
  busy: boolean
}) {
  const [name, setName] = useState('')

  return (
    <div className="appsuite-dialog-backdrop">
      <div className="appsuite-dialog">
        <h3 className="appsuite-dialog__title">New Project</h3>
        <p className="appsuite-dialog__sub">Give your project a name to get started.</p>
        <input
          className="appsuite-dialog__input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Project name"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim() && !busy) onConfirm(name.trim())
            if (e.key === 'Escape') onCancel()
          }}
        />
        <div className="appsuite-dialog__actions">
          <button
            type="button"
            className="appsuite-dialog__cancel"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="appsuite-dialog__confirm"
            disabled={!name.trim() || busy}
            onClick={() => onConfirm(name.trim())}
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}

const IFRAME_W = 390
const IFRAME_H = 844
const PREVIEW_PAD = 12 // padding inside preview box (all sides)

function PreviewThumbnail({ projectName, templateId }: { projectName: string; templateId: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [scale, setScale] = useState(0)

  const measure = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const availableW = el.clientWidth - PREVIEW_PAD * 2
    setScale(availableW / IFRAME_W)
  }, [])

  useEffect(() => {
    measure()
    const ro = new ResizeObserver(measure)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [measure])


  return (
    <div ref={containerRef} className="appsuite-project-card__preview">
      {scale > 0 ? (
        <iframe
          ref={iframeRef}
          title={`Preview ${projectName}`}
          src={`/appsuite/preview-embed/${encodeURIComponent(templateId)}?source=published&demo=1`}
          className="appsuite-project-card__preview-iframe"
          tabIndex={-1}
          style={{
            width: IFRAME_W,
            height: IFRAME_H,
            top: PREVIEW_PAD,
            left: PREVIEW_PAD,
            transform: `scale(${scale})`,
          }}
        />
      ) : null}
      <div className="appsuite-project-card__preview-overlay" />
    </div>
  )
}

export default function AppSuiteProjectsPage() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<AppSuiteProject[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState('')
  const [showNewDialog, setShowNewDialog] = useState(false)
  const [creatingProject, setCreatingProject] = useState(false)

  const clientId = getAppSuiteActiveClientId()

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const all = await listAppSuiteProjects()
      const filtered = clientId
        ? all.filter((p) => p.clientId === clientId)
        : all
      setProjects(filtered)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load projects')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId])

  const handleDelete = async (project: AppSuiteProject) => {
    if (!window.confirm(`Delete project "${project.name}"? This cannot be undone.`)) return
    setDeletingId(project.id)
    try {
      const result = await deleteAppSuiteProject(project.id)
      if (!result.success) throw new Error(result.error || 'Delete failed')
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setDeletingId('')
    }
  }

  const handleCreateProject = async (name: string) => {
    if (!clientId) {
      setError('請先在 Users 頁選擇 Client')
      return
    }
    setCreatingProject(true)
    setError(null)
    try {
      const result = await createAppSuiteProject({ name, clientId, platform: 'iphone' })
      if (!result.success || !result.project?.id) {
        throw new Error(result.error || 'Failed to create project')
      }
      setShowNewDialog(false)
      navigate(`/appsuite/projects/${result.project.id}/edit`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create project')
    } finally {
      setCreatingProject(false)
    }
  }

  return (
    <div className="appsuite-projects-page appfactory-page-content">
      <div className="appsuite-projects-header">
        <div>
          <h2>My Projects</h2>
          <p>Projects created from Marketplace templates.</p>
        </div>
        <button
          type="button"
          className="appsuite-projects-new-btn"
          onClick={() => {
            if (!clientId) {
              setError('請先在 Users 頁選擇 Client')
              return
            }
            setShowNewDialog(true)
          }}
        >
          <span className="material-symbols-rounded">add</span>
          New Project
        </button>
      </div>

      {error ? <div className="appsuite-published-error">{error}</div> : null}
      {loading ? <div className="appsuite-published-loading">Loading projects...</div> : null}

      {!loading && !clientId ? (
        <div className="appsuite-published-empty">
          請先在 Users 頁選擇 Client 以顯示其專案。
        </div>
      ) : null}

      {!loading && clientId && projects.length === 0 && !error ? (
        <div className="appsuite-projects-empty">
          <span className="material-symbols-rounded appsuite-projects-empty__icon">folder_open</span>
          <p>No projects yet.</p>
          <button
            type="button"
            className="appsuite-tmpl-card__clone-btn appsuite-projects-empty__cta"
            onClick={() => setShowNewDialog(true)}
          >
            Create First Project
          </button>
        </div>
      ) : null}

      {!loading && projects.length > 0 ? (
        <div className="appsuite-projects-grid">
          {projects.map((project) => {
            const templateId = project.templateSource?.templateId || ''
            return (
              <div
                key={project.id}
                className="appsuite-project-card appsuite-project-card--has-preview"
                onClick={() => navigate(`/appsuite/projects/${project.id}/edit`)}
              >
                {/* Mini preview */}
                {templateId ? (
                  <PreviewThumbnail projectName={project.name} templateId={templateId} />
                ) : (
                  <div className="appsuite-project-card__preview appsuite-project-card__preview--empty">
                    <span className="material-symbols-rounded">phone_iphone</span>
                    <span>No template</span>
                  </div>
                )}

                {/* Info */}
                <div className="appsuite-project-card__info">
                  <div className="appsuite-project-card__header">
                    <span className="appsuite-project-card__name">{project.name}</span>
                    <span className={statusBadgeClass(project.status)}>
                      {project.status || 'draft'}
                    </span>
                  </div>
                  <span className="appsuite-project-card__date">
                    {project.updatedAt ? `Edited ${formatDate(project.updatedAt)}` : `Created ${formatDate(project.createdAt)}`}
                    {project.createdByName ? ` · ${project.createdByName}` : ''}
                  </span>
                </div>

                {/* Actions */}
                <div className="appsuite-project-card__actions">
                  <button
                    type="button"
                    className="appsuite-project-card__edit-btn"
                    onClick={(e) => { e.stopPropagation(); navigate(`/appsuite/projects/${project.id}/edit`) }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="appsuite-project-card__delete-btn"
                    disabled={deletingId === project.id}
                    onClick={(e) => { e.stopPropagation(); void handleDelete(project) }}
                  >
                    {deletingId === project.id ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      ) : null}

      {showNewDialog ? (
        <NewProjectDialog
          onConfirm={(name) => void handleCreateProject(name)}
          onCancel={() => setShowNewDialog(false)}
          busy={creatingProject}
        />
      ) : null}
    </div>
  )
}
